import { useCallback, useEffect, useRef, useState } from 'react';
import { applyMessage, type RawOrchestratorSnapshot } from '../model/normalize';
import type { MatchingSessionState } from '../model/types';
import type { MatchingConnection, MatchingTransport } from '../transport/types';
import { INVALID_TOKEN_CLOSE_CODE, nextRetry } from './reconnect';

export type ConnectionStatus =
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'failed';

interface UseMatchingSessionOptions {
  /** The `matching_url` the create call returned, if we just created the session. Used once. */
  initialUrl?: string | null;
  /** Mints a fresh `matching_url` (tokens are single-use). Normally `resume`. */
  mintUrl: () => Promise<string>;
  transport: MatchingTransport;
}

function describeError(err: unknown, closeCode?: number): string {
  if (closeCode === INVALID_TOKEN_CLOSE_CODE)
    return "The matching service didn't accept this session's sign-in. Try again, or reload the page.";
  const e = err as { code?: string; message?: string } | null;
  if (e?.code === 'MATCHING_SESSION_EXPIRED')
    return 'This matching session has expired.';
  if (e?.code === 'MATCHING_SESSION_NOT_FOUND')
    return 'This matching session no longer exists.';
  if (e?.message) return e.message;
  return "We couldn't connect to the matching service.";
}

/**
 * The live connection to a matching-server session.
 *
 * 1. Gets a token: the create call's `matching_url` the first time, otherwise
 *    `mintUrl()` (every reconnect needs a fresh single-use token).
 * 2. Opens the transport.
 * 3. Folds every message into one typed `MatchingSessionState`.
 * 4. Reconnects with backoff on an unexpected close (see `nextRetry`). A
 *    connection counts as working only once its first message arrives, and
 *    a refused token (close code 4001) stops instead of retrying.
 */
export function useMatchingSession({
  initialUrl,
  mintUrl,
  transport,
}: UseMatchingSessionOptions) {
  const [current, setCurrent] = useState<{
    state: MatchingSessionState;
    raw: RawOrchestratorSnapshot;
  } | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const connRef = useRef<MatchingConnection | null>(null);
  const initialUsedRef = useRef(false);
  const mintRef = useRef(mintUrl);

  useEffect(() => {
    mintRef.current = mintUrl;
  }, [mintUrl]);

  useEffect(() => {
    let cancelled = false;
    let failures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRetry = (err: unknown, closeCode?: number) => {
      failures += 1;
      const decision = nextRetry(failures, closeCode);
      if (!decision.retry) {
        setConnection('failed');
        setError(describeError(err, closeCode));
        return;
      }
      setConnection('reconnecting');
      retryTimer = setTimeout(connect, decision.delayMs);
    };

    async function connect() {
      // Wait one microtask before using a token. React StrictMode (dev)
      // mounts, cleans up and remounts effects synchronously; without this
      // the throwaway first mount would spend the single-use create token on
      // a socket it closes at once, and the real mount would have to re-mint.
      await Promise.resolve();
      if (cancelled) return;

      let url: string;
      try {
        if (initialUrl && !initialUsedRef.current) {
          initialUsedRef.current = true;
          url = initialUrl;
        } else {
          url = await mintRef.current();
        }
      } catch (err) {
        if (!cancelled) scheduleRetry(err);
        return;
      }
      if (cancelled) return;

      // matching-server accepts every socket and then closes a bad one, so
      // the connection only counts as working once the first message lands.
      let received = false;
      connRef.current = transport.open(url, {
        onOpen: () => {},
        onMessage: msg => {
          if (cancelled) return;
          if (!received) {
            received = true;
            failures = 0;
            setConnection('open');
            setError(null);
          }
          setCurrent(prev => applyMessage(prev, msg));
        },
        onClose: ({ code }) => {
          if (cancelled) return;
          connRef.current = null;
          scheduleRetry(null, code);
        },
      });
    }

    connect();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      connRef.current?.close();
      connRef.current = null;
    };
  }, [transport, initialUrl, attempt]);

  /** Sends to the Orchestrator. Returns false when the connection isn't open. */
  const send = useCallback((method: string, arg?: unknown): boolean => {
    return connRef.current?.send({ method, arg }) ?? false;
  }, []);

  const retry = useCallback(() => {
    setConnection('connecting');
    setError(null);
    setAttempt(a => a + 1);
  }, []);

  return { session: current?.state ?? null, connection, error, send, retry };
}
